import { createSelector } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import { ITechOperationSubGroupDto } from "src/pages/tech-operations/shared/dtos/tech-operation-sub-group.dto";

import { baseQuery } from "../../../shared/api/base-query";
import { filterNotDeleted } from "../../../shared/api/filter-not-deleted";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";

export const techOperationSubGroupsApi = createApi({
  reducerPath: "techOperationSubGroupsApi",
  baseQuery,
  endpoints: (builder) => ({
    fetchTechOperationSubGroupsWithDeleted: builder.query<
      ITechOperationSubGroupDto[],
      void
    >({
      queryFn: pagedQueryFn<ITechOperationSubGroupDto, ITechOperationSubGroupDto[]>(
        { url: "TechOperationSubGroupsV2", params: { includeDeleted: true } },
        (dtos) => dtos
      ),
    }),
  }),
});

const selectNotDeletedData = createSelector(
  ({ data }: { data?: ITechOperationSubGroupDto[] }) => data,
  filterNotDeleted<ITechOperationSubGroupDto>
);

const selectNotDeletedCurrentData = createSelector(
  ({ currentData }: { currentData?: ITechOperationSubGroupDto[] }) => currentData,
  filterNotDeleted<ITechOperationSubGroupDto>
);

const useTechOperationSubGroupsEndpoint =
  techOperationSubGroupsApi.endpoints.fetchTechOperationSubGroupsWithDeleted.useQuery;

type TechOperationSubGroupsQueryOptions = QuerySubscriptionOptions<
  typeof useTechOperationSubGroupsEndpoint
>;

export function useFetchTechOperationSubGroups(
  options?: TechOperationSubGroupsQueryOptions
) {
  return useTechOperationSubGroupsEndpoint(undefined, {
    ...options,
    selectFromResult: (result) => ({
      ...result,
      data: selectNotDeletedData(result),
      currentData: selectNotDeletedCurrentData(result),
    }),
  });
}

export function useFetchTechOperationSubGroupsWithDeleted(
  options?: TechOperationSubGroupsQueryOptions
) {
  return useTechOperationSubGroupsEndpoint(undefined, options);
}
